<template>
  <BaseCard>
    <Flex
      :direction="$screen === 's' ? 'column' : 'row'"
      :align="$screen === 's' ? 'stretch' : 'center'"
      justify="between"
    >
      <Flex align="center">
        <BaseIconTile name="credit-card" />
        <Flex
          direction="column"
          :gap=".25"
        >
          <Flex
            :gap="0.5"
            align="center"
            wrap
          >
            <BaseText
              bold
              v-test="'sp-wallet-name'"
            >
              {{ name }}
            </BaseText>
            <BaseLabel
              v-if="balanceAccount.isDefault"
              color="primary"
              v-test="'sp-wallet-main-label'"
            >
              {{ $t('register.salonized_pay.dashboard.wallets.main_wallet') }}
            </BaseLabel>
            <BaseLabel
              v-if="isClosing"
              color="warning"
              v-test="'sp-wallet-closing-label'"
            >
              {{ $t('register.salonized_pay.dashboard.wallets.closing') }}
            </BaseLabel>
          </Flex>
          <BaseText
            v-if="shareText"
            color="secondary"
            v-test="'sp-wallet-share'"
          >
            {{ shareText }}
          </BaseText>
        </Flex>
      </Flex>

      <Flex
        :gap="2"
        align="center"
      >
        <Flex
          direction="column"
          :align="$screen === 's' ? 'start' : 'end'"
          :gap="0.25"
        >
          <BaseHeading size="s">
            {{ $t('global.available') }}
          </BaseHeading>
          <BaseText
            size="l"
            bold
            v-test="'sp-wallet-available-balance'"
          >
            <BaseSpinner
              v-if="loading"
              inline
            />
            <template v-else>
              {{ filters.currency(availableBalance, currency) }}
            </template>
          </BaseText>
        </Flex>
        <Flex
          direction="column"
          :align="$screen === 's' ? 'start' : 'end'"
          :gap="0.25"
        >
          <BaseHeading size="s">
            {{ $t('global.pending') }}
          </BaseHeading>
          <BaseText
            size="l"
            bold
            color="secondary"
            v-test="'sp-wallet-processing-balance'"
          >
            <BaseSpinner
              v-if="loading"
              inline
            />
            <template v-else>
              {{ filters.currency(processingBalance, currency) }}
            </template>
          </BaseText>
        </Flex>
        <div
          v-if="canEdit && !balanceAccount.isDefault && !isClosing"
          :class="$style.more"
        >
          <BaseMore
            :options="moreOptions"
            @select="onMoreSelect"
            v-test="'sp-wallet-more'"
          />
        </div>
      </Flex>
    </Flex>

    <template #footer>
      <BaseText
        v-if="verificationNotice"
        :color="verificationNotice.color"
        iconBefore="alert-triangle"
        v-test="'sp-wallet-verification'"
      >
        {{ verificationNotice.text }}
      </BaseText>
      <BaseText
        v-else
        color="secondary"
        v-test="'sp-wallet-payout-schedule'"
      >
        {{ payoutScheduleText }}
      </BaseText>
    </template>
  </BaseCard>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Option } from '@/components/BaseMore.vue';
import filters from '@/filters';
import { brandNamePay } from '@/helpers/brand-name';
import { useAdyenStore } from '@/stores/adyen';
import {
  type AdyenBalanceAccount,
  AdyenBalanceAccountStatus,
  AdyenTransferInstrumentVerificationStatus,
  type Currency,
} from '@/types';
import { GET_ADYEN_WALLET_BALANCE } from '../graphql';
import { formatShare, walletName } from './wallet-share';

const props = defineProps<{
  balanceAccount: AdyenBalanceAccount;
  canEdit: boolean;
}>();

const emit = defineEmits<{
  edit: [];
  remove: [];
  viewPayouts: [];
}>();

const { t } = useI18n();

const name = computed(() => walletName(props.balanceAccount));

const isClosing = computed(
  () => props.balanceAccount.status === AdyenBalanceAccountStatus.Closing,
);

const moreOptions = computed<Option[]>(() => [
  { name: 'edit', icon: 'edit', label: t('global.actions.edit') },
  {
    name: 'view-payouts',
    icon: 'activity',
    label: t('register.salonized_pay.dashboard.wallets.view_payouts'),
  },
  {
    name: 'remove',
    icon: 'delete',
    label: t('register.salonized_pay.dashboard.wallets.remove'),
  },
]);

const onMoreSelect = (option: string) => {
  if (option === 'edit') {
    emit('edit');
  } else if (option === 'remove') {
    emit('remove');
  } else if (option === 'view-payouts') {
    emit('viewPayouts');
  }
};

const shareText = computed(() => {
  // A closing wallet's share has already stopped.
  if (isClosing.value) {
    return t('register.salonized_pay.dashboard.wallets.closing_description');
  }

  if (props.balanceAccount.isDefault) {
    return t(
      'register.salonized_pay.dashboard.wallets.main_wallet_description',
    );
  }

  const paymentRule = props.balanceAccount.paymentRule;

  if (!paymentRule) {
    return '';
  }

  return paymentRule.employee
    ? t('register.salonized_pay.dashboard.wallets.team_member_share', {
        ...brandNamePay(),
        share: formatShare(paymentRule.basisPoints),
        name: paymentRule.employee.name,
      })
    : t('register.salonized_pay.dashboard.wallets.share', {
        ...brandNamePay(),
        share: formatShare(paymentRule.basisPoints),
      });
});

const currency = computed(
  () => (props.balanceAccount.currency as Currency) || undefined,
);

const { result, loading } = useQuery(GET_ADYEN_WALLET_BALANCE, {
  balanceAccountId: String(props.balanceAccount.id),
});

const availableBalance = computed(
  () => result.value?.adyenDashboardData?.availableBalance || 0,
);

const processingBalance = computed(
  () => result.value?.adyenDashboardData?.processingBalance || 0,
);

const scheduleKeys: Record<string, string> = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  BALANCE: 'balance',
};

const payoutScheduleText = computed(() => {
  const sweep = props.balanceAccount.adyenSweep;
  const last4 = sweep?.adyenTransferInstrument?.accountNumberLast4;

  if (!sweep || !last4) {
    return t(
      'register.salonized_pay.dashboard.wallets.payout_schedule.unconfigured',
    );
  }

  const scheduleKey = scheduleKeys[sweep.scheduleType ?? ''];

  return t(
    `register.salonized_pay.dashboard.wallets.payout_schedule.${scheduleKey}`,
    { destination: `•••• ${last4}` },
  );
});

const transferInstrument = computed(
  () => props.balanceAccount.adyenSweep?.adyenTransferInstrument,
);

const adyenStore = useAdyenStore();

// Removed bank accounts are no longer listed on the app state, but a wallet's
// sweep keeps pointing at one until it is given a new bank account.
const transferInstrumentRemoved = computed(
  () =>
    adyenStore.adyenAppStateLoaded &&
    !!transferInstrument.value &&
    !(adyenStore.adyenAppState.transferInstruments ?? []).some(
      (instrument) => instrument.id === transferInstrument.value?.id,
    ),
);

// A valid or not yet known verification just shows the payout schedule, as
// does a wallet without a bank account so its unconfigured message stays.
const verificationNotice = computed(() => {
  const instrument = transferInstrument.value;
  const last4 = instrument?.accountNumberLast4;

  if (!last4) {
    return null;
  }

  const destination = `•••• ${last4}`;

  if (transferInstrumentRemoved.value) {
    return {
      color: 'error' as const,
      text: t('register.salonized_pay.dashboard.wallets.bank_account_removed', {
        destination,
      }),
    };
  }

  switch (instrument.verification?.status) {
    case AdyenTransferInstrumentVerificationStatus.Pending:
      return {
        color: 'warning' as const,
        text: t(
          'register.salonized_pay.dashboard.wallets.verification.pending',
          { destination },
        ),
      };
    case AdyenTransferInstrumentVerificationStatus.Invalid:
      return {
        color: 'error' as const,
        text: t(
          'register.salonized_pay.dashboard.wallets.verification.invalid',
          { destination },
        ),
      };
    case AdyenTransferInstrumentVerificationStatus.Rejected:
      return {
        color: 'error' as const,
        text: t(
          'register.salonized_pay.dashboard.wallets.verification.rejected',
          { destination },
        ),
      };
    default:
      return null;
  }
});
</script>

<style
  lang="scss"
  module
>
.more {
  margin-left: auto;
}
</style>
